import React, { useState, useEffect } from 'react';
import { Shield, Play, Activity, CheckCircle, AlertTriangle, Terminal } from 'lucide-react';
import apiClient from '../../api/apiClient';

/**
 * OpenAEVSimulator
 * Interface for interacting with OpenAEV scenarios.
 * Displays available scenarios, allows starting them, and shows results.
 */
const OpenAEVSimulator = ({ scenarios: initialScenarios, onComplete, estimatedTime, challenge }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [availableScenarios, setAvailableScenarios] = useState([]);
    const [activeSimulation, setActiveSimulation] = useState(null);
    const [statusTimer, setStatusTimer] = useState(null);

    // Load scenarios on mount
    useEffect(() => {
        fetchScenarios();
        return () => {
            if (statusTimer) clearInterval(statusTimer);
        };
    }, []);

    const fetchScenarios = async () => {
        try {
            setLoading(true);
            // NOTE: We use the backend proxy endpoint
            const response = await apiClient.get('/openaev/scenarios');
            if (response.data && response.data.success) {
                setAvailableScenarios(response.data.data);
            } else {
                // Fallback or error handling
                setError('Failed to load scenarios from OpenAEV service.');
            }
        } catch (err) {
            console.error('Error fetching OpenAEV scenarios:', err);
            // If we are in a demo/dev environment without backend, we might want to fallback locally?
            // For now, show error.
            setError('Could not connect to OpenAEV service.');
        } finally {
            setLoading(false);
        }
    };

    const startSimulation = async (scenario) => {
        try {
            setLoading(true);
            const response = await apiClient.post('/openaev/simulation/start', {
                scenarioId: scenario.id
            });

            if (response.data && response.data.success) {
                setActiveSimulation(response.data.data);
                startPolling(response.data.data.simulation_id);
            } else {
                setError('Failed to start simulation.');
            }
        } catch (err) {
            console.error('Error starting simulation:', err);
            setError('Error starting simulation.');
        } finally {
            setLoading(false);
        }
    };

    const startPolling = (simulationId) => {
        const timer = setInterval(async () => {
            try {
                const response = await apiClient.get(`/openaev/simulation/${simulationId}`);
                if (response.data && response.data.success) {
                    const status = response.data.data;
                    setActiveSimulation(prev => ({ ...prev, ...status }));

                    if (status.status === 'completed') {
                        clearInterval(timer);
                        finishSimulation(simulationId);
                    }
                }
            } catch (err) {
                console.error('Error polling status:', err);
            }
        }, 2000); // Poll every 2 seconds
        setStatusTimer(timer);
    };

    const finishSimulation = async (simulationId) => {
        try {
            const response = await apiClient.get(`/openaev/simulation/${simulationId}/results`);
            if (response.data && response.data.success) {
                const results = response.data.data;
                // Map OpenAEV results to the format expected by SecuritySimulationContainer
                const summary = {
                    total: 1,
                    correct: results.score >= 70 ? 1 : 0,
                    incorrect: results.score < 70 ? 1 : 0,
                    score: results.score,
                    details: results,
                    message: `OpenAEV Simulation Completed. Detected Threats: ${results.detected_threats}`
                };
                onComplete(summary);
            }
        } catch (err) {
            console.error('Error fetching results:', err);
        }
    };

    if (loading && !activeSimulation) {
        return (
            <div className="flex flex-col items-center justify-center p-12 space-y-4">
                <Activity className="w-12 h-12 text-blue-500 animate-spin" />
                <p className="text-gray-400">Communicating with OpenAEV Platform...</p>
            </div>
        );
    }

    if (activeSimulation) {
        return (
            <div className="max-w-4xl mx-auto bg-gray-800 rounded-xl overflow-hidden border border-gray-700 p-8">
                <div className="flex items-center space-x-4 mb-6">
                    <div className="p-3 bg-blue-900/50 rounded-lg animate-pulse">
                        <Terminal className="w-8 h-8 text-blue-400" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-white">Simulation Running</h2>
                        <p className="text-gray-400">Scenario ID: {activeSimulation.scenario_id}</p>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
                        <div
                            className="bg-blue-500 h-full transition-all duration-500 ease-out"
                            style={{ width: `${activeSimulation.progress || 0}%` }}
                        ></div>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4 font-mono text-sm max-h-60 overflow-y-auto space-y-2">
                        {activeSimulation.events && activeSimulation.events.map((event, idx) => (
                            <div key={idx} className="flex space-x-2 text-gray-300">
                                <span className="text-gray-500">[{new Date(event.timestamp).toLocaleTimeString()}]</span>
                                <span>{event.message}</span>
                            </div>
                        ))}
                        {(!activeSimulation.events || activeSimulation.events.length === 0) && (
                            <div className="text-gray-500 italic">Waiting for events stream...</div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto">
            <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-white mb-2">OpenAEV Integration</h2>
                <p className="text-gray-400 max-w-2xl mx-auto">
                    Select an adversarial simulation scenario to execute. These scenarios run on our OpenAEV platform
                    to validation your detection and response capabilities.
                </p>
            </div>

            {error && (
                <div className="bg-red-900/30 border border-red-500/50 text-red-200 p-4 rounded-lg mb-6 flex items-center">
                    <AlertTriangle className="mr-2" /> {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableScenarios.map(scenario => (
                    <div key={scenario.id} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-blue-500/50 transition-all hover:shadow-[0_0_20px_rgba(59,130,246,0.15)] group">
                        <div className="p-6">
                            <div className="mb-4 flex justify-between items-start">
                                <div className="p-2 bg-gray-700 rounded-lg group-hover:bg-blue-900/30 group-hover:text-blue-400 transition-colors">
                                    <Shield size={24} />
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full border ${scenario.difficulty === 'Advanced' ? 'border-red-500/30 text-red-400 bg-red-900/10' :
                                        scenario.difficulty === 'Intermediate' ? 'border-yellow-500/30 text-yellow-400 bg-yellow-900/10' :
                                            'border-green-500/30 text-green-400 bg-green-900/10'
                                    }`}>
                                    {scenario.difficulty}
                                </span>
                            </div>

                            <h3 className="text-xl font-bold text-white mb-2">{scenario.name}</h3>
                            <p className="text-gray-400 text-sm mb-4 line-clamp-2">{scenario.description}</p>

                            <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
                                <span className="flex items-center"><Activity size={14} className="mr-1" /> {scenario.type}</span>
                                <span className="flex items-center"><Terminal size={14} className="mr-1" /> {scenario.estimated_duration}</span>
                            </div>

                            <button
                                onClick={() => startSimulation(scenario)}
                                className="w-full py-2 bg-gray-700 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center justify-center font-medium"
                            >
                                <Play size={16} className="mr-2" /> Launch Simulation
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OpenAEVSimulator;
